/* eslint-disable no-console */
import { useToast } from '@cian/ui-kit';
import * as React from 'react';
import {
  EGetReferralLinkRejectStatus,
  getReferralLink,
  getReferralListing,
  requestAuthentication,
} from '../../../../thunks/referrer';
import { getWebview } from '@cian/webview/webview';
import { useReferrerAppDispatch } from '../../../../hooks';
import { useSelector } from 'react-redux';
import { selectIsWebview, selectUserId } from '../../../../selectors';
import { trackCopyLinkClick, trackCopySuccessError, trackCopySuccessShow } from '../tracking';
import { getBoundingClientRect } from '../../../../utils/getBoundingClientRect';
import { setReferralLinkFallbackModalOpen } from '../../../../slices';

export const useGetReferralLink = (btn?: React.RefObject<HTMLButtonElement | null>) => {
  const dispatch = useReferrerAppDispatch();
  const toastApi = useToast({ placement: 'top-right' });

  const userId = useSelector(selectUserId);
  const isWebview = useSelector(selectIsWebview);

  const copyReferralLink = React.useCallback(async () => {
    trackCopyLinkClick();
    const link = await dispatch(getReferralLink('copy'));

    if (link.payload === EGetReferralLinkRejectStatus.ERROR) {
      toastApi.open({ type: 'error', text: 'Что-то пошло не так — попробуйте ещё раз' });
      trackCopySuccessError();

      return;
    }

    if (link.payload === EGetReferralLinkRejectStatus.NOT_AUTHORIZED) {
      // Неавторизованный пользователь - не показываем тост
      return;
    }

    if (isWebview) {
      const webview = getWebview();
      webview.sendAction({
        action: 'copy-clipboard',
        payload: {
          text: link.payload as string,
        },
      });

      toastApi.open({ type: 'success', text: 'Ссылка скопирована' });
      trackCopySuccessShow();

      return;
    }

    if (window?.navigator?.clipboard?.writeText) {
      try {
        await window.navigator.clipboard.writeText(link.payload as string);

        toastApi.open({ type: 'success', text: 'Ссылка скопирована' });
        trackCopySuccessShow();
      } catch {
        dispatch(setReferralLinkFallbackModalOpen(link.payload as string));
      }
    }
  }, [dispatch, isWebview]);

  const shareReferralLink = React.useCallback(async () => {
    trackCopyLinkClick();
    const link = await dispatch(getReferralLink('share'));

    if (link.payload === EGetReferralLinkRejectStatus.ERROR) {
      toastApi.open({ type: 'error', text: 'Что-то пошло не так — попробуйте ещё раз' });
      trackCopySuccessError();

      return;
    }

    if (link.payload === EGetReferralLinkRejectStatus.NOT_AUTHORIZED) {
      // Неавторизованный пользователь - не показываем тост
      return;
    }

    if (isWebview && btn) {
      const webview = getWebview();
      const parameters = getBoundingClientRect(btn.current);

      webview.sendAction({
        action: 'share-text',
        payload: {
          text: link.payload as string,
          parameters,
        },
      });

      return;
    }

    if (window?.navigator) {
      await window.navigator.share({ text: link.payload as string });

      return;
    }
  }, []);

  const handleCopyLinkClick = React.useCallback(async () => {
    if (!userId) {
      dispatch(
        requestAuthentication({
          viewData: { headingType: 'cpdReferral' },
          onSuccess: async () => {
            await dispatch(getReferralListing());
            await copyReferralLink();
          },
        }),
      );
    } else {
      await copyReferralLink();
    }
  }, [dispatch, userId, copyReferralLink]);

  const handleShareLinkClick = React.useCallback(async () => {
    if (!userId) {
      dispatch(
        requestAuthentication({
          viewData: { headingType: 'cpdReferral' },
          onSuccess: async () => {
            await dispatch(getReferralListing());
            await shareReferralLink();
          },
        }),
      );
    } else {
      await shareReferralLink();
    }
  }, [dispatch, userId, shareReferralLink]);

  return { copyReferralLink, shareReferralLink, handleCopyLinkClick, handleShareLinkClick };
};
